import React, { useCallback, useEffect, useState } from "react";
import { Activity, RefreshCw, CheckCircle2, XCircle, Webhook, Images, DatabaseZap } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 0) return "just now";
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
}

function Row({ icon: Icon, label, value, tone = "text-slate-900" }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2.5 text-sm text-slate-500">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className={`text-sm font-semibold font-mono-num ${tone}`}>{value}</div>
    </div>
  );
}

export default function SyncHealthPanel() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sync-health");
      setHealth(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!health) {
    return <div className="text-sm text-slate-400 py-10 text-center" data-testid="sync-health-loading">Loading sync health…</div>;
  }

  const healthy = health.configured && health.status === "live" && health.webhooks.failed_24h === 0;
  const statusTone = health.status === "live" ? "text-emerald-600" : health.status === "error" ? "text-rose-600" : "text-amber-600";

  return (
    <div className="space-y-6" data-testid="sync-health-panel">
      <div className={`flex items-center justify-between rounded-2xl border p-5 ${healthy ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
        <div className="flex items-center gap-3">
          {healthy
            ? <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            : <Activity className="h-6 w-6 text-amber-600" />}
          <div>
            <div className="font-heading font-bold text-slate-900" data-testid="sync-health-summary">
              {healthy ? "Zoho sync is healthy" : "Sync needs attention"}
            </div>
            <div className="text-xs text-slate-500">
              {health.configured ? "Connected to Zoho Inventory" : "Zoho not connected"}
              {health.message ? ` · ${health.message}` : ""}
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-full" data-testid="sync-health-refresh-button">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <DatabaseZap className="h-4 w-4" /> Catalogue Sync
          </h3>
          <Row icon={Activity} label="Status" value={health.status} tone={statusTone} />
          <Row icon={RefreshCw} label="Last full sync" value={timeAgo(health.last_synced)} />
          <Row icon={CheckCircle2} label="Items in last sync" value={health.items_synced ?? 0} />
          <Row icon={DatabaseZap} label="Products updated (24h)" value={health.products_synced_24h} />
          <Row icon={Images} label="Products missing cover image" value={health.blank_covers}
               tone={health.blank_covers > 0 ? "text-amber-600" : "text-emerald-600"} />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <Webhook className="h-4 w-4" /> Live Webhooks
          </h3>
          <Row icon={Webhook} label="Received (24h)" value={health.webhooks.received_24h} />
          <Row icon={CheckCircle2} label="Processed OK (24h)" value={health.webhooks.processed_24h} tone="text-emerald-600" />
          <Row icon={XCircle} label="Failed (24h)" value={health.webhooks.failed_24h}
               tone={health.webhooks.failed_24h > 0 ? "text-rose-600" : "text-emerald-600"} />
          <Row icon={Activity} label="Last webhook" value={timeAgo(health.webhooks.last_received_at)} />
        </div>
      </div>

      {health.images && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="sync-health-images">
          <h3 className="font-heading font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <Images className="h-4 w-4" /> Product Photos (website storage)
          </h3>
          <Row icon={Activity} label="Photo download job" value={health.images.status} tone={health.images.status === "running" ? "text-amber-600" : "text-emerald-600"} />
          <Row icon={DatabaseZap} label="Designs in stock" value={health.images.designs_listed} />
          <Row icon={CheckCircle2} label="Designs with photos" value={health.images.designs_with_photos} tone="text-emerald-600" />
          <Row icon={Images} label="Designs without photos yet" value={health.images.designs_without_photos}
               tone={health.images.designs_without_photos > 0 ? "text-amber-600" : "text-emerald-600"} />
          <Row icon={Images} label="Photos stored (WebP)" value={health.images.images_stored} />
          <Row icon={XCircle} label="Photos failed (auto-retry)" value={health.images.images_failed}
               tone={health.images.images_failed > 0 ? "text-rose-600" : "text-emerald-600"} />
          <Row icon={RefreshCw} label="Last photo run" value={health.images.last_run ? timeAgo(new Date(health.images.last_run * 1000).toISOString()) : "never"} />
        </div>
      )}

      {health.recent_failures.length > 0 && (
        <div className="bg-white rounded-2xl border border-rose-200 p-5" data-testid="sync-health-failures">
          <h3 className="font-heading font-semibold text-rose-700 mb-3 flex items-center gap-2">
            <XCircle className="h-4 w-4" /> Recent webhook failures
          </h3>
          <div className="space-y-1.5">
            {health.recent_failures.map((f, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-rose-50 rounded-lg px-3 py-2">
                <span className="font-mono-num text-slate-600">Item {f.item_id || "—"}</span>
                <span className="text-slate-400">{timeAgo(f.received_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400">
        "Sync Zoho now" pulls items, then downloads new photos into website storage. Photos keep downloading every 30 min (throttled to respect Zoho limits) until every in-stock design has them.
      </p>
    </div>
  );
}
